<template>
    <div>
        <!-- Boîte à outils et filtres au-dessus de l'arbre -->
        <div class="card border-0 shadow-sm mb-4" style="border-radius: 12px;">
            <div class="card-header bg-white border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                <div>
                    <h2 class="h5 font-weight-bold mb-1 text-dark">
                        <i class="far fa-sitemap mr-2 text-primary"></i> Arborescence des zones de stockage
                    </h2>
                    <p class="text-muted small mb-0">Cliquez sur une zone pour afficher ses sous-zones. Glissez-déposez pour réorganiser (avant, après ou dans une zone).</p>
                </div>
                <div>
                    <button v-if="canCreate" class="ix-btn blue" @click="openCreateModal(null)">
                        <i class="far fa-plus"></i> <span class="d-none d-sm-inline ml-1">Nouvelle zone</span>
                    </button>
                </div>
            </div>
            
            <div class="card-body px-4 py-3">
                <!-- Barre de recherche -->
                <div class="input-group mb-3">
                    <div class="input-group-prepend">
                        <span class="input-group-text bg-white border-right-0" style="border-top-left-radius: 8px; border-bottom-left-radius: 8px;">
                            <i class="far fa-search text-muted"></i>
                        </span>
                    </div>
                    <input type="text" 
                           class="form-control border-left-0" 
                           placeholder="Rechercher par code, nom de zone ou article (nom, ref)..." 
                           v-model="searchQuery"
                           style="border-top-right-radius: 8px; border-bottom-right-radius: 8px;">
                </div>

                <!-- Loader de chargement -->
                <div v-if="loading" class="text-center py-5">
                    <i class="far fa-circle-notch fa-spin fa-2x text-primary mb-2"></i>
                    <p class="text-muted mb-0">Chargement des zones de stockage...</p>
                </div>

                <!-- Message d'erreur -->
                <div v-else-if="error" class="alert alert-danger">
                    <i class="far fa-exclamation-triangle mr-2"></i> {{ error }}
                    <button class="btn btn-sm btn-outline-danger float-right" @click="fetchEmplacements" style="margin-top: -3px;">Réessayer</button>
                </div>

                <!-- Aucune zone -->
                <div v-else-if="flatTree.length === 0 && !canCreate" class="text-center py-5 text-muted">
                    <i class="far fa-map-marker-slash mb-3" style="font-size: 40px; color: #cbd5e1;"></i>
                    <p class="mb-0">Aucune zone de stockage trouvée.</p>
                </div>

                <!-- Recherche : liste des zones trouvées (par nom, code ou article), un clic ouvre la zone -->
                <div v-else-if="searchQuery.trim()" class="family-columns-search">
                    <div v-if="searchResults.length === 0" class="text-muted small px-3 py-2">Aucune zone ne correspond.</div>
                    <div v-for="node in searchResults"
                         :key="node.id"
                         class="family-col-row"
                         :class="{ 'is-inactive': node.is_inactive_branch }"
                         @click="selectFromSearch(node)">
                        <i class="far col-row-icon mr-3" :class="getNodeIcon(node)" :style="getNodeIconStyle(node)"></i>
                        <span class="col-row-name">{{ node.full_path || node.name }}</span>
                        <span v-if="node.code" class="code-badge ml-2">{{ node.code }}</span>
                        <span v-if="getMatchingArticlesForNode(node).length > 0" class="ml-2 d-flex flex-wrap align-items-center">
                            <span v-for="art in getMatchingArticlesForNode(node)" :key="'match-art-' + art.id" class="badge badge-warning text-dark mr-1 py-1 px-2" style="font-size: 10px;">
                                <i class="far fa-box mr-1" style="font-size: 9px;"></i> {{ art.name }}<span v-if="art.number" class="text-muted ml-1">#{{ art.number }}</span>
                            </span>
                        </span>
                        <span class="col-row-count ml-auto">{{ totalArticles[node.id] || 0 }}</span>
                    </div>
                </div>

                <!-- Vue en colonnes : zones racines, puis les sous-zones de chaque zone sélectionnée -->
                <div v-else class="family-columns" ref="columns">
                    <div v-for="(column, colIndex) in columns"
                         :key="'col-' + (column.parentId || 'root')"
                         class="family-column"
                         :class="{ 'is-drop-column': dropColumnKey === (column.parentId || 'root') }"
                         @dragover="onColumnDragOver($event, column)"
                         @dragleave="onColumnDragLeave($event)"
                         @drop="onColumnDrop($event, column)">
                        <div v-for="node in column.nodes"
                             :key="node.id"
                             class="family-col-row"
                             :class="{
                                'is-selected': selectedPath[colIndex] === node.id,
                                'is-empty': !totalArticles[node.id],
                                'is-inactive': node.is_inactive_branch,
                                'is-dragging': draggedNodeId === node.id,
                                'is-drop-inside': dropTargetId === node.id && dropPosition === 'inside',
                                'is-drop-before': dropTargetId === node.id && dropPosition === 'before',
                                'is-drop-after': dropTargetId === node.id && dropPosition === 'after'
                             }"
                             :draggable="canEdit"
                             @dragstart="onDragStart($event, node)"
                             @dragend="onDragEnd"
                             @dragover.stop="onColumnRowDragOver($event, colIndex, node)"
                             @drop.stop="onColumnRowDrop($event, node)"
                             @click="selectColumnNode($event, colIndex, node)">
                            <i class="far col-row-icon mr-3" :class="getNodeIcon(node)" :style="getNodeIconStyle(node)"></i>
                            <span v-if="node.code" class="code-badge mr-2">{{ node.code }}</span>
                            <i v-if="node.is_default" class="far fa-star mr-1 text-warning" title="Zone de stockage par défaut" style="font-size: 12px;"></i>
                            <span class="col-row-name" :class="{ 'text-strikethrough': !node.is_active }">{{ node.name }}</span>
                            <span v-if="!node.is_active" class="ix-state red ml-2">Inactif</span>

                            <div class="col-row-actions ml-auto">
                                <a class="col-row-action" :href="'/stocks/emplacements/' + node.id" title="Consulter">
                                    <i class="far fa-search"></i>
                                </a>
                                <button v-if="canEdit" type="button" class="col-row-action" title="Modifier" @click="openEditModal(node)">
                                    <i class="far fa-pen"></i>
                                </button>
                                <button v-if="canDelete && !node.articles_count" type="button" class="col-row-action danger" title="Supprimer" @click="deleteEmplacement(node)">
                                    <i class="far fa-trash"></i>
                                </button>
                            </div>

                            <span class="col-row-count ml-2" title="Articles (sous-zones incluses)">{{ totalArticles[node.id] || 0 }}</span>
                            <span class="col-row-chevron ml-2">
                                <i v-if="node.children.length > 0" class="far fa-chevron-right"></i>
                            </span>
                        </div>

                        <!-- Création inline en bas de colonne -->
                        <template v-if="canCreate && columnAcceptsCreate(column)">
                            <div v-if="columnCreateKey === (column.parentId || 'root')" class="family-col-create flex-column align-items-stretch" @click.stop>
                                <div v-if="inlineError" class="text-danger small mb-1"><i class="far fa-exclamation-circle mr-1"></i> {{ inlineError }}</div>
                                <div class="d-flex align-items-center">
                                    <input type="text"
                                           class="form-control form-control-sm inline-create-input column-create-input"
                                           v-model="inlineCreateName"
                                           :disabled="inlineSubmitting"
                                           :placeholder="column.parentId ? 'Nom de la sous-zone...' : 'Nom de la zone...'"
                                           @keyup.enter="submitInlineCreate"
                                           @keyup.esc="cancelInlineCreate">
                                    <input type="text"
                                           class="form-control form-control-sm inline-create-code ml-2"
                                           v-model="inlineCreateCode"
                                           :disabled="inlineSubmitting"
                                           placeholder="Code"
                                           title="Code d'identification"
                                           @keyup.enter="submitInlineCreate"
                                           @keyup.esc="cancelInlineCreate"
                                           style="width: 90px;">
                                    <button class="ix-btn green ml-2" @click="submitInlineCreate" :disabled="inlineSubmitting || !inlineCreateName.trim() || !inlineCreateCode.trim()">
                                        <i class="far" :class="inlineSubmitting ? 'fa-circle-notch fa-spin' : 'fa-check'"></i>
                                    </button>
                                    <button class="ix-btn transparent ml-1" @click="cancelInlineCreate" :disabled="inlineSubmitting" title="Annuler">
                                        <i class="far fa-times"></i>
                                    </button>
                                </div>
                            </div>
                            <div v-else class="family-col-row is-new" @click="startColumnCreate(column.parentId)">
                                <i class="far fa-plus col-row-icon mr-3"></i>
                                <span>{{ column.parentId ? 'Nouvelle sous-zone' : 'Nouvelle zone' }}</span>
                            </div>
                        </template>

                        <div v-if="column.nodes.length === 0 && !(canCreate && columnAcceptsCreate(column))" class="text-muted small px-3 py-2">
                            Aucune sous-zone.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Boîte modale de Création / Modification d'un emplacement -->
        <div class="modal fade" id="emplacementFormModal" tabindex="-1" role="dialog" aria-hidden="true" style="z-index: 1060;">
            <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div class="modal-content border-0 shadow-lg" style="border-radius: 12px;">
                    <div class="modal-header bg-light border-0 py-3 px-4">
                        <h5 class="modal-title font-weight-bold text-dark">
                            <i :class="isEditing ? 'far fa-map-marker-alt text-success' : 'far fa-map-marker-plus text-primary'" class="mr-2"></i>
                            {{ isEditing ? 'Modifier la zone de stockage' : 'Créer une zone de stockage' }}
                        </h5>
                        <button type="button" class="close" @click="closeModal" aria-label="Close">
                            <i class="fal fa-times"></i>
                        </button>
                    </div>
                    
                    <div class="modal-body px-4 py-4">
                        <!-- Message d'erreur -->
                        <div v-if="modalError" class="alert alert-danger mb-3 d-flex align-items-center">
                            <i class="far fa-exclamation-circle mr-2 h5 mb-0"></i> {{ modalError }}
                        </div>

                        <div class="ix-form-container">
                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label font-weight-bold text-dark">Zone parent</label>
                                <div class="col-sm-8">
                                    <select class="form-control" v-model="form.parent_id">
                                        <option :value="null">[ Aucun - Niveau racine ]</option>
                                        <option v-for="parent in parentOptions" 
                                                :key="parent.id" 
                                                :value="parent.id"
                                                :disabled="(isEditing && (parent.id === editingId || isDescendantOf(parent.id, editingId))) || !parent.is_active">
                                            {{ parent.full_path || parent.name }}{{ !parent.is_active ? ' (Inactif)' : '' }}
                                        </option>
                                    </select>
                                    <small v-if="isEditing" class="form-text text-muted mt-1">Une zone ne peut pas avoir pour parent elle-même ou l'une de ses descendantes.</small>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label required font-weight-bold text-dark">Nom de la zone de stockage</label>
                                <div class="col-sm-8">
                                    <input type="text" class="form-control" v-model="form.name" required>
                                    <small class="form-text text-muted">Ex: Étagère A, Bac 3, Allée 2...</small>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label required font-weight-bold text-dark">Code d'identification (Scan / QR)</label>
                                <div class="col-sm-8">
                                    <input type="text" class="form-control text-uppercase" v-model="form.code" required @input="codeManuallyEdited = true">
                                    <small class="form-text text-muted">Ex: ETG-A, BAC-3... Sert de clé d'identification unique pour les scans et codes-barres.</small>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label font-weight-bold text-dark">Description</label>
                                <div class="col-sm-8">
                                    <textarea class="form-control" v-model="form.description" rows="3"></textarea>
                                    <small class="form-text text-muted">Informations additionnelles sur la localisation.</small>
                                </div>
                            </div>

                            <!-- Section Adresse -->
                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label font-weight-bold text-dark">Adresse</label>
                                <div class="col-sm-8">
                                    <div class="input-group">
                                        <input type="text"
                                               class="form-control model-autocomplete"
                                               v-model="form.address_label"
                                               data-force-value="false"
                                               data-api-url="/api/addresses/autocomplete"
                                               data-label="designation"
                                               data-value="line_formatted"
                                               data-target-input-id="emplacement-address-id">
                                        <input type="hidden" v-model="form.address_id" id="emplacement-address-id">
                                    </div>
                                </div>
                            </div>

                            <!-- Géolocalisation : saisie, position de l'appareil ou clic sur la carte -->
                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label font-weight-bold text-dark">Géolocalisation</label>
                                <div class="col-sm-8">
                                    <geolocation-picker :latitude="form.latitude" :longitude="form.longitude" :visible="modalShown" @change="onGeolocationChange"></geolocation-picker>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label class="col-sm-4 col-form-label font-weight-bold text-dark">Statut</label>
                                <div class="col-sm-8 d-flex align-items-center">
                                    <div class="form-check form-check-inline mr-3">
                                        <input class="form-check-input" type="radio" id="statusActive" :value="true" v-model="form.is_active">
                                        <label class="form-check-label font-weight-normal mb-0" for="statusActive">Actif</label>
                                    </div>
                                    <div class="form-check form-check-inline">
                                        <input class="form-check-input" type="radio" id="statusInactive" :value="false" v-model="form.is_active">
                                        <label class="form-check-label font-weight-normal mb-0" for="statusInactive">Inactif</label>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group row mb-0">
                                <label class="col-sm-4 col-form-label font-weight-bold text-dark">Zone par défaut</label>
                                <div class="col-sm-8">
                                    <div class="form-check">
                                        <input class="form-check-input" type="checkbox" id="isDefault" :true-value="true" :false-value="false" v-model="form.is_default">
                                        <label class="form-check-label font-weight-normal mb-0" for="isDefault">Définir comme zone de stockage par défaut</label>
                                        <small class="form-text text-muted d-block">Une seule zone peut être par défaut. Si activé, la zone par défaut actuelle sera désactivée.</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer bg-light border-0 py-3 px-4 d-flex justify-content-between">
                        <button type="button" class="ix-btn transparent" @click="closeModal">Annuler</button>
                        <button type="button" class="ix-btn" :class="isEditing ? 'green' : 'blue'" @click="submitForm" :disabled="submitting || !form.name">
                            <span v-if="submitting"><i class="far fa-circle-notch fa-spin mr-2"></i> Enregistrement...</span>
                            <span v-else>{{ isEditing ? 'Mettre à jour' : 'Créer la zone' }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import $ from "jquery";
import GeolocationPicker from "../GeolocationPicker.vue";

export default {
    name: 'emplacement-tree-manager',
    components: { GeolocationPicker },
    props: {
        canCreate: {
            type: Boolean,
            default: false
        },
        canEdit: {
            type: Boolean,
            default: false
        },
        canDelete: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            emplacements: [],
            loading: true,
            error: null,
            searchQuery: '',
            // Vue en colonnes : zones ouvertes (une par colonne), création inline, survol pendant un glisser-déposer
            selectedPath: [],
            columnCreateKey: null,
            dropColumnKey: null,
            hoverOpenTimer: null,
            
            
            // État du glisser-déposer (Drag & Drop)
            draggedNodeId: null,
            dropTargetId: null,
            dropPosition: null,
            
            // État de la création inline
            inlineCreateParentId: null,
            inlineCreateName: '',
            inlineCreateCode: '',
            inlineSubmitting: false,
            inlineError: '',
            codeRequestId: 0,

            // État de la modale
            isEditing: false,
            editingId: null,
            submitting: false,
            modalError: '',
            codeManuallyEdited: false,
            // Modale affichée : la carte de géolocalisation recalcule sa taille
            modalShown: false,
            form: {
                name: '',
                code: '',
                parent_id: null,
                icon: null,
                description: '',
                is_active: true,
                is_default: false,
                address_id: null,
                address_label: '',
                latitude: null,
                longitude: null
            }
        };
    },
    computed: {
        // Renvoie l'arborescence plate ordonnée
        flatTree() {
            const map = {};
            this.emplacements.forEach(emp => {
                map[emp.id] = { ...emp, children: [] };
            });

            const roots = [];
            this.emplacements.forEach(emp => {
                const node = map[emp.id];
                if (node.parent_id && map[node.parent_id]) {
                    map[node.parent_id].children.push(node);
                } else {
                    roots.push(node);
                }
            });

            const flat = [];
            const traverse = (node, depth, path) => {
                node.depth = depth;
                node.path = path;
                flat.push(node);
                if (node.children && node.children.length > 0) {
                    node.children.forEach(child => {
                        traverse(child, depth + 1, [...path, node.id]);
                    });
                }
            };

            roots.forEach(root => {
                traverse(root, 0, []);
            });

            return flat;
        },
        
        nodeById() {
            const map = {};
            this.flatTree.forEach(node => { map[node.id] = node; });
            return map;
        },

        // Nombre d'articles par zone, sous-zones incluses
        totalArticles() {
            const totals = {};
            const count = node => {
                totals[node.id] = (node.articles_count || 0)
                    + node.children.reduce((sum, child) => sum + count(child), 0);
                return totals[node.id];
            };
            this.flatTree.filter(node => node.depth === 0).forEach(count);
            return totals;
        },

        // Colonnes affichées : zones racines, puis les sous-zones de chaque zone sélectionnée
        columns() {
            const cols = [{ parentId: null, nodes: this.flatTree.filter(node => node.depth === 0) }];
            this.selectedPath.forEach(id => {
                const node = this.nodeById[id];
                if (node && (node.children.length > 0 || (this.canCreate && !node.is_inactive_branch))) {
                    cols.push({ parentId: id, nodes: node.children });
                }
            });
            return cols;
        },

        // Recherche : zones dont le nom ou le code correspond, ou qui contiennent un article correspondant
        searchResults() {
            const query = this.searchQuery.trim().toLowerCase();
            return this.flatTree.filter(node => {
                return node.name.toLowerCase().includes(query)
                    || (node.code && node.code.toLowerCase().includes(query))
                    || this.getMatchingArticlesForNode(node).length > 0;
            });
        },

        // Options éligibles pour l'emplacement parent
        parentOptions() {
            return this.emplacements.map(e => ({
                id: e.id,
                name: e.name,
                full_path: e.full_path,
                parent_id: e.parent_id,
                is_active: e.is_active
            })).sort((a, b) => a.name.localeCompare(b.name));
        }
    },
    watch: {
        'form.name': function(newVal) {
            this.generateDefaultCode();
        },
        'form.parent_id': function(newVal) {
            this.generateDefaultCode();
        },
        'inlineCreateName': function(newVal) {
            this.generateInlineDefaultCode();
        }
    },
    methods: {
        selectColumnNode(event, colIndex, node) {
            if (event.target.closest('.col-row-actions')) return;
            this.cancelInlineCreate();
            this.selectedPath = [...this.selectedPath.slice(0, colIndex), node.id];
            this.scrollColumnsToEnd();
        },

        selectFromSearch(node) {
            this.searchQuery = '';
            this.selectedPath = [...node.path, node.id];
            this.scrollColumnsToEnd();
        },

        // Création possible à la racine et sous une zone active (pas dans une branche inactive)
        columnAcceptsCreate(column) {
            if (!column.parentId) return true;
            const parent = this.nodeById[column.parentId];
            return parent && !parent.is_inactive_branch;
        },

        startColumnCreate(parentId) {
            this.cancelInlineCreate();
            this.columnCreateKey = parentId || 'root';
            this.inlineCreateParentId = parentId;
            this.$nextTick(() => {
                const input = this.$el.querySelector('.column-create-input');
                if (input) input.focus();
            });
        },

        scrollColumnsToEnd() {
            this.$nextTick(() => {
                const el = this.$refs.columns;
                if (el) el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
            });
        },

        // Retire de la sélection les zones supprimées ou déplacées
        pruneSelection() {
            const valid = [];
            for (const id of this.selectedPath) {
                const emp = this.emplacements.find(e => e.id === id);
                if (!emp || (emp.parent_id || null) !== (valid.length ? valid[valid.length - 1] : null)) break;
                valid.push(id);
            }
            this.selectedPath = valid;
        },

        cancelInlineCreate() {
            this.columnCreateKey = null;
            this.inlineCreateParentId = null;
            this.inlineCreateName = '';
            this.inlineCreateCode = '';
            this.inlineSubmitting = false;
            this.inlineError = '';
        },

        generateInlineDefaultCode() {
            const name = this.inlineCreateName.trim();
            if (!name) {
                this.inlineCreateCode = '';
                return;
            }
            let prefix = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").substring(0, 3).toUpperCase();
            prefix = prefix.replace(/[^A-Z]/g, '');
            if (prefix.length < 3) {
                prefix = (prefix + 'EMP').substring(0, 3);
            }
            const count = this.emplacements.filter(e => e.parent_id === this.inlineCreateParentId).length;
            this.inlineCreateCode = `${prefix}-${count + 1}`;
        },

        async submitInlineCreate() {
            if (!this.inlineCreateName.trim() || !this.inlineCreateCode.trim() || this.inlineSubmitting) return;
            this.inlineSubmitting = true;

            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

            try {
                const response = await fetch('/stocks/emplacements', {
                    method: 'POST',
                    body: JSON.stringify({
                        name: this.inlineCreateName.trim(),
                        code: this.inlineCreateCode.trim(),
                        parent_id: this.inlineCreateParentId,
                        is_active: true
                    }),
                    ...this.getHttpJsonRequestHeaders(),
                    headers: {
                        ...this.getHttpJsonRequestHeaders().headers,
                        'X-CSRF-TOKEN': csrfToken
                    }
                });
                let data;
                try {
                    data = await response.json();
                } catch (e) {
                    throw new Error("Une erreur serveur est survenue. Vérifiez l'unicité du code.");
                }
                if (!response.ok) {
                    if (data.errors) {
                        const firstKey = Object.keys(data.errors)[0];
                        throw new Error(data.errors[firstKey][0]);
                    }
                    throw new Error(data.message || "Erreur lors de la création.");
                }
                if (data.success) {
                    if (window.toastr) window.toastr.success(data.message || 'Zone créée avec succès.');
                    this.inlineCreateName = '';
                    this.inlineCreateCode = '';
                    this.inlineCreateParentId = null;
                    this.columnCreateKey = null;
                    await this.fetchEmplacements(true);
                }
            } catch (error) {
                this.inlineError = error.message;
                if (window.toastr) window.toastr.error(error.message);
            } finally {
                this.inlineSubmitting = false;
            }
        },

        generateDefaultCode() {
            if (this.isEditing || this.codeManuallyEdited) return;
            
            const name = this.form.name.trim();
            if (!name) {
                this.form.code = '';
                return;
            }
            
            let prefix = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").substring(0, 3).toUpperCase();
            prefix = prefix.replace(/[^A-Z]/g, '');
            if (prefix.length < 3) {
                prefix = (prefix + 'EMP').substring(0, 3);
            }
            
            const parentId = this.form.parent_id;
            const count = this.emplacements.filter(e => e.parent_id === parentId).length;
            
            this.form.code = `${prefix}-${count + 1}`;
        },
        // L'écran de gestion affiche aussi les zones inactives (grisées), pour pouvoir les réactiver
        fetchEmplacements(silent = false) {
            this.loading = !silent;
            this.error = null;

            return fetch('/api/emplacements/tree?include_inactive=1', {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(response => {
                if (!response.ok) throw new Error("Impossible de charger les emplacements.");
                return response.json();
            })
            .then(data => {
                if (data.success) {
                    this.emplacements = data.emplacements;
                    this.pruneSelection();
                } else {
                    this.error = "Erreur de format de réponse.";
                }
                this.loading = false;
            })
            .catch(error => {
                this.error = error.message || "Une erreur est survenue.";
                this.loading = false;
            });
        },

        isDescendantOf(candidateParentId, currentId) {
            // Vérifie si un candidat parent est un enfant de l'emplacement actuel (pour éviter les boucles)
            let current = this.emplacements.find(e => e.id === candidateParentId);
            while (current) {
                if (current.parent_id === currentId) return true;
                current = this.emplacements.find(e => e.id === current.parent_id);
            }
            return false;
        },

        // --- Glisser-déposer (vue en colonnes) : avant / dans / après une zone, ordre enregistré ---
        onDragStart(event, node) {
            if (!this.canEdit) return;
            this.draggedNodeId = node.id;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(node.id));
        },

        onDragEnd() {
            clearTimeout(this.hoverOpenTimer);
            this.dropColumnKey = null;
            this.draggedNodeId = null;
            this.dropTargetId = null;
            this.dropPosition = null;
        },

        // Dépôt possible à la racine, ou dans une zone active qui n'est ni la zone déplacée ni l'une de ses sous-zones
        canDropIntoFor(draggedId, parentId) {
            if (parentId === null) return true;
            const parent = this.nodeById[parentId];
            if (!parent || parent.is_inactive_branch) return false;
            return parentId !== draggedId && !this.isDescendantOf(parentId, draggedId);
        },

        canDropInto(parentId) {
            return !!this.draggedNodeId && this.canDropIntoFor(this.draggedNodeId, parentId);
        },

        clearDropTarget() {
            clearTimeout(this.hoverOpenTimer);
            this.dropTargetId = null;
            this.dropPosition = null;
        },

        onColumnRowDragOver(event, colIndex, node) {
            if (!this.draggedNodeId) return;
            this.dropColumnKey = null;

            if (node.id === this.draggedNodeId) {
                // Survol de soi-même : le dépôt est autorisé mais sans effet
                event.preventDefault();
                this.clearDropTarget();
                return;
            }
            if (!this.canDropInto(node.parent_id || null)) return;

            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';

            const rect = event.currentTarget.getBoundingClientRect();
            const offsetY = event.clientY - rect.top;
            let position;
            if (offsetY < rect.height * 0.3) {
                position = 'before';
            } else if (offsetY > rect.height * 0.7) {
                position = 'after';
            } else {
                position = this.canDropInto(node.id) ? 'inside' : (offsetY < rect.height / 2 ? 'before' : 'after');
            }

            // Survol prolongé au centre : ouvre la zone pour pouvoir déposer plus profond
            if (position === 'inside' && (this.dropTargetId !== node.id || this.dropPosition !== 'inside')) {
                clearTimeout(this.hoverOpenTimer);
                this.hoverOpenTimer = setTimeout(() => {
                    if (this.draggedNodeId && this.dropTargetId === node.id && this.dropPosition === 'inside') {
                        this.selectedPath = [...this.selectedPath.slice(0, colIndex), node.id];
                        this.scrollColumnsToEnd();
                    }
                }, 700);
            }

            this.dropTargetId = node.id;
            this.dropPosition = position;
        },

        onColumnRowDrop(event, node) {
            event.preventDefault();
            const draggedId = this.draggedNodeId;
            const position = this.dropPosition;
            const targetId = this.dropTargetId;
            this.onDragEnd();
            if (!draggedId || targetId !== node.id || !position) return;
            this.dropOnNode(draggedId, node, position);
        },

        // Survol de l'espace vide d'une colonne : ajout en fin de colonne
        onColumnDragOver(event, column) {
            if (!this.canDropInto(column.parentId)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
            this.clearDropTarget();
            this.dropColumnKey = column.parentId || 'root';
        },

        onColumnDragLeave(event) {
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.dropColumnKey = null;
            }
        },

        onColumnDrop(event, column) {
            event.preventDefault();
            const draggedId = this.draggedNodeId;
            this.onDragEnd();
            if (!draggedId || !this.canDropIntoFor(draggedId, column.parentId)) return;
            const siblings = column.nodes.map(n => n.id).filter(id => id !== draggedId);
            this.moveEmplacement(draggedId, column.parentId, [...siblings, draggedId]);
        },

        // Calcule le nouvel ordre des zones sœurs selon la position de dépôt
        dropOnNode(draggedId, targetNode, position) {
            if (draggedId === targetNode.id) return;

            if (position === 'inside') {
                const siblings = targetNode.children.map(n => n.id).filter(id => id !== draggedId);
                return this.moveEmplacement(draggedId, targetNode.id, [...siblings, draggedId]);
            }

            const parentId = targetNode.parent_id || null;
            const siblings = this.flatTree
                .filter(n => (n.parent_id || null) === parentId && n.id !== draggedId)
                .map(n => n.id);
            let index = siblings.indexOf(targetNode.id);
            if (position === 'after') index++;
            siblings.splice(index, 0, draggedId);
            return this.moveEmplacement(draggedId, parentId, siblings);
        },

        async moveEmplacement(draggedId, parentId, orderedIds) {
            const dragged = this.emplacements.find(e => e.id === draggedId);
            if (!dragged) return;

            const unchanged = (dragged.parent_id || null) === parentId
                && this.flatTree.filter(n => (n.parent_id || null) === parentId).map(n => n.id).join(',') === orderedIds.join(',');
            if (unchanged) return;

            // Mise à jour optimiste pour éviter le rechargement complet
            this.emplacements = this.emplacements
                .map(e => {
                    const index = orderedIds.indexOf(e.id);
                    if (index === -1) return e;
                    return { ...e, parent_id: e.id === draggedId ? parentId : e.parent_id, position: index + 1 };
                })
                .sort((a, b) => (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name));
            this.pruneSelection();

            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            try {
                const response = await fetch('/stocks/emplacements/reorder', {
                    method: 'POST',
                    body: JSON.stringify({ parent_id: parentId, ids: orderedIds }),
                    ...this.getHttpJsonRequestHeaders(),
                    headers: {
                        ...this.getHttpJsonRequestHeaders().headers,
                        'X-CSRF-TOKEN': csrfToken
                    }
                });
                const data = await response.json();
                if (!response.ok || !data.success) throw new Error(data.message || "Erreur lors du déplacement.");
                if (window.toastr) window.toastr.success(data.message);
            } catch (error) {
                if (window.toastr) window.toastr.error(error.message);
            }
            this.fetchEmplacements(true);
        },

        getMatchingArticlesForNode(node) {
            const query = this.searchQuery.trim().toLowerCase();
            if (!query || !node.articles) return [];
            return node.articles.filter(art => {
                const artName = art.name ? art.name.toLowerCase() : '';
                const artNum = art.number ? art.number.toLowerCase() : '';
                const artRef = art.reference ? art.reference.toLowerCase() : '';
                return artName.includes(query) || artNum.includes(query) || artRef.includes(query);
            });
        },

        getNodeIcon(node) {
            if (node.icon) return node.icon;
            
            // Fallback par défaut basé sur la profondeur de l'arbre
            if (node.depth === 0) return 'far fa-warehouse';
            if (node.depth === 1) return 'far fa-folder-open';
            return 'far fa-cube';
        },

        getNodeIconStyle(node) {
            // Palette de couleurs élégantes selon l'icône
            const iconClass = node.icon || this.getNodeIcon(node);
            
            if (iconClass.includes('warehouse')) return { color: '#0284c7', fontSize: '14px' };
            if (iconClass.includes('folder')) return { color: '#d97706', fontSize: '14px' };
            if (iconClass.includes('cube')) return { color: '#4b5563', fontSize: '13px', opacity: 0.8 };
            if (iconClass.includes('boxes')) return { color: '#16a34a', fontSize: '14px' };
            if (iconClass.includes('archive')) return { color: '#7c3aed', fontSize: '14px' };
            if (iconClass.includes('pallet')) return { color: '#b45309', fontSize: '14px' };
            if (iconClass.includes('truck')) return { color: '#ea580c', fontSize: '14px' };
            
            return { color: '#4b5563', fontSize: '14px' }; // Gris ardoise par défaut
        },

        openCreateModal(parentId = null) {
            this.cancelInlineCreate();
            this.isEditing = false;
            this.editingId = null;
            this.modalError = '';
            this.codeManuallyEdited = false;
            this.form = {
                name: '',
                code: '',
                parent_id: parentId,
                icon: null,
                description: '',
                is_active: true,
                is_default: false,
                address_id: null,
                address_label: '',
                latitude: null,
                longitude: null
            };
            $('#emplacementFormModal').modal('show');
        },

        openEditModal(node) {
            this.isEditing = true;
            this.editingId = node.id;
            this.modalError = '';
            this.codeManuallyEdited = true;
            this.form = {
                name: node.name,
                code: node.code || '',
                parent_id: node.parent_id,
                icon: node.icon || null,
                description: node.description || '',
                is_active: node.is_active,
                is_default: node.is_default || false,
                address_id: node.address_id || null,
                address_label: node.address_label || '',
                latitude: node.latitude,
                longitude: node.longitude
            };
            $('#emplacementFormModal').modal('show');
        },

        onGeolocationChange(position) {
            this.form.latitude = position.latitude;
            this.form.longitude = position.longitude;
        },

        closeModal() {
            $('#emplacementFormModal').modal('hide');
        },

        submitForm() {
            if (!this.form.name) return;
            
            // Récupérer address_id depuis le DOM car model-autocomplete (jQuery) 
            // modifie le hidden input en dehors de la réactivité Vue
            const addressIdInput = this.$el.querySelector('#emplacement-address-id');
            if (addressIdInput) {
                this.form.address_id = addressIdInput.value || null;
            }
            
            this.submitting = true;
            this.modalError = '';
            
            const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
            const url = this.isEditing ? `/stocks/emplacements/${this.editingId}` : '/stocks/emplacements';
            const method = this.isEditing ? 'PUT' : 'POST';

            fetch(url, {
                method: method,
                body: JSON.stringify(this.form),
                ...this.getHttpJsonRequestHeaders(),
                headers: {
                    ...this.getHttpJsonRequestHeaders().headers,
                    'X-CSRF-TOKEN': csrfToken
                }
            })
            .then(async response => {
                const data = await response.json();
                if (!response.ok) {
                    if (data.errors) {
                        const firstKey = Object.keys(data.errors)[0];
                        throw new Error(data.errors[firstKey][0]);
                    }
                    throw new Error(data.message || "Une erreur est survenue.");
                }
                return data;
            })
            .then(data => {
                this.submitting = false;
                if (data && data.success) {
                    this.closeModal();
                    if (window.toastr) {
                        window.toastr.success(data.message);
                    }
                    
                    // Une sous-zone créée : sa zone parente est ouverte pour la montrer
                    const parentNode = !this.isEditing && this.form.parent_id ? this.nodeById[this.form.parent_id] : null;
                    if (parentNode) {
                        this.selectedPath = [...parentNode.path, parentNode.id];
                    }

                    this.fetchEmplacements(true);
                } else {
                    this.modalError = "Une erreur est survenue lors de l'enregistrement.";
                }
            })
            .catch(error => {
                this.submitting = false;
                this.modalError = error.message || "Une erreur serveur est survenue.";
            });
        },

        deleteEmplacement(node) {
            let warningText = `La zone "${node.name}" sera supprimée.`;
            if (node.children && node.children.length > 0) {
                warningText = `La zone "${node.name}" sera supprimée. Ses sous-zones sont conservées et remontent d'un niveau.`;
            }

            this.$swal({
                title: "Êtes vous sûr ?",
                text: warningText,
                icon: "warning",
                dangerMode: true,
                buttons: ["Annuler", "Supprimer"]
            }).then((willDelete) => {
                if (willDelete) {
                    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
                    
                    fetch(`/stocks/emplacements/${node.id}`, {
                        method: 'DELETE',
                        ...this.getHttpJsonRequestHeaders(),
                        headers: {
                            ...this.getHttpJsonRequestHeaders().headers,
                            'X-CSRF-TOKEN': csrfToken
                        }
                    })
                    .then(async response => {
                        if (response.redirected) {
                            return { success: true, message: "La zone a été supprimée." };
                        }
                        
                        try {
                            const data = await response.json();
                            if (!response.ok) {
                                throw new Error(data.message || "Erreur lors de la suppression.");
                            }
                            return data;
                        } catch (e) {
                            if (response.ok) return { success: true };
                            throw new Error("Une erreur est survenue.");
                        }
                    })
                    .then(data => {
                        if (window.toastr) {
                            window.toastr.success(data.message || "La zone de stockage a été supprimée avec succès.");
                        }
                        this.fetchEmplacements(true);
                    })
                    .catch(error => {
                        this.$swal({
                            title: 'Erreur',
                            text: error.message || "Une erreur est survenue lors de la suppression.",
                            icon: 'error'
                        });
                    });
                }
            });
        }
    },
    mounted() {
        this.fetchEmplacements();

        $('#emplacementFormModal')
            .on('shown.bs.modal', () => { this.modalShown = true; })
            .on('hidden.bs.modal', () => { this.modalShown = false; });
    }
};
</script>

<style scoped>
.emplacement-tree-list {
    user-select: none;
}

.emplacement-tree-container {
    max-height: 700px;
    overflow-y: auto;
    border: 1px solid #f1f5f9;
    border-radius: 8px;
    padding: 8px;
    background: #fafbfc;
}

.emplacement-node-row {
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
    border-radius: 8px !important;
    border: 1px solid transparent;
    background: #fff;
}

.emplacement-node-row:hover {
    background-color: #f1f5f9 !important;
}

.emplacement-node-row[draggable="true"] {
    cursor: grab;
}
.emplacement-node-row[draggable="true"]:active {
    cursor: grabbing;
}
.emplacement-node-row.is-dragging {
    cursor: grabbing !important;
    opacity: 0.4;
}

/* Zone chevron */
.chevron-area {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
}
.chevron-area.has-children {
    cursor: pointer;
}
.chevron-icon {
    transition: transform 0.2s ease;
    font-size: 12px;
}

/* Icône emplacement */
.node-icon {
    font-size: 15px;
}

/* Badge code */
.code-badge {
    font-family: Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-weight: 700;
    font-size: 11px;
    background-color: #e2e8f0;
    color: #334155;
    padding: 3px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Nom */
.node-name {
    font-size: 13.5px;
    color: #334155;
    line-height: 1.4;
}
.node-name.node-depth-0 {
    font-weight: 700;
    font-size: 14.5px;
    color: #1e293b;
}
.node-name.node-depth-1 {
    font-weight: 600;
}

/* Badge nombre d'articles */
.child-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background-color: #e2e8f0;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
}

/* Actions - visibles au survol de la ligne ou quand le dropdown est ouvert */
.node-actions-group {
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
}
.emplacement-node-row:hover .node-actions-group,
.node-actions-group .btn-group.show {
    opacity: 1;
}

/* Drag and drop visuels */
.emplacement-node-row.is-drop-target {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.emplacement-node-row.is-drop-before {
    border-top: 3px solid #3b82f6;
}
.emplacement-node-row.is-drop-after {
    border-bottom: 3px solid #3b82f6;
}

/* Transitions de liste */
.tree-list-enter-active, .tree-list-leave-active {
    transition: all 0.25s ease;
}
.tree-list-enter, .tree-list-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Ligne de création inline */
.inline-create-row {
    animation: slideDown 0.2s ease;
    background-color: #f0f7ff;
    border: 1px solid #bfdbfe;
    margin-left: 4px;
}
@keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
}
.inline-create-input {
    border: 1px solid #93c5fd !important;
    border-radius: 6px !important;
    font-size: 13.5px !important;
    height: 34px;
}
.inline-create-input:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}
.inline-create-code {
    border: 1px solid #93c5fd !important;
    border-radius: 6px !important;
    font-size: 13px !important;
    height: 34px;
    text-align: center;
    font-family: Menlo, Monaco, Consolas, monospace;
}
.inline-create-code:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}

.text-strikethrough {
    text-decoration: line-through;
}

/* Assurer que l'icône de fermeture est visible et stylée */
.close {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
    color: #000;
    text-shadow: 0 1px 0 #fff;
    opacity: .5;
    background-color: transparent;
    border: 0;
}
.close:hover {
    opacity: .75;
}

/* Vue en colonnes (reprise des familles d'articles) */
/* Actions au survol (vue colonnes) */
.col-row-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #64748b;
    font-size: 12px;
    cursor: pointer;
}
.col-row-action:hover {
    background-color: #fff;
    color: #1d4ed8;
    text-decoration: none;
}
.col-row-action.danger:hover {
    color: #dc2626;
}

/* Drag and drop (vue colonnes) */
.family-col-row[draggable="true"] {
    cursor: grab;
}
.family-col-row {
    border: 1px solid transparent;
}
.family-col-row.is-dragging {
    opacity: 0.4;
}
.family-col-row.is-drop-inside {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
.family-col-row.is-drop-before {
    box-shadow: inset 0 3px 0 #3b82f6;
}
.family-col-row.is-drop-after {
    box-shadow: inset 0 -3px 0 #3b82f6;
}
.family-column.is-drop-column {
    background-color: #f8fbff;
    box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.25);
}

/* Vue en colonnes */
.family-columns {
    display: flex;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow-x: auto;
    min-height: 260px;
    user-select: none;
}
.family-column {
    flex: 1 0 320px;
    max-width: 480px;
    padding: 10px;
    border-right: 1px solid #e2e8f0;
}
.family-column:last-child {
    border-right: 0;
}
.family-columns-search {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 10px;
}
.family-col-row {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    margin-bottom: 2px;
    border-radius: 8px;
    cursor: pointer;
    color: #1e293b;
    font-size: 14.5px;
    transition: background-color 0.15s ease;
}
.family-col-row:hover {
    background-color: #f1f5f9;
}
.family-col-row.is-selected {
    background-color: #e8effa;
    color: #1d4ed8;
}
.family-col-row.is-empty:not(.is-selected) {
    color: #94a3b8;
}
.family-col-row.is-new {
    color: #94a3b8;
    font-size: 13.5px;
}
.family-col-row.is-new:hover {
    color: #3b82f6;
}
.col-row-icon {
    width: 16px;
    text-align: center;
    color: #64748b;
}
.family-col-row.is-selected .col-row-icon {
    color: #1d4ed8;
}
.family-col-row.is-empty:not(.is-selected) .col-row-icon,
.family-col-row.is-new .col-row-icon {
    color: #cbd5e1;
}
.col-row-nomenclature {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #94a3b8;
}
.col-row-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.col-row-count {
    min-width: 30px;
    height: 24px;
    padding: 0 9px;
    border-radius: 12px;
    background-color: #f1f5f9;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.family-col-row.is-selected .col-row-count {
    background-color: #fff;
    color: #1d4ed8;
}
.col-row-chevron {
    width: 14px;
    font-size: 12px;
    color: #94a3b8;
}
.family-col-row.is-selected .col-row-chevron {
    color: #1d4ed8;
}
.col-row-actions {
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
}
.family-col-row:hover .col-row-actions,
.col-row-actions .btn-group.show {
    opacity: 1;
}
.family-col-create {
    display: flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: 8px;
    background-color: #f0f7ff;
    border: 1px solid #bfdbfe;
}
/* Zones inactives (et leurs sous-zones) : grisées */
.family-col-row.is-inactive {
    opacity: 0.6;
}
.text-strikethrough {
    text-decoration: line-through;
}
</style>
